import {
  createContext,
  useContext,
  useState,
  useEffect,
  type ReactNode,
} from "react";

export type AIProvider = "gemini" | "openrouter";

// OpenRouter free models with fallback support
export const OPENROUTER_MODELS = [
  {
    id: "liquid/lfm-2.5-1.2b-thinking:free",
    name: "Liquid LFM 2.5 Thinking",
    default: true,
  },
  { id: "openai/gpt-4.1-nano:free", name: "OpenAI GPT-4.1 Nano" },
  { id: "xiaomi/mimo-v2-flash:free", name: "Xiaomi MiMo V2 Flash" },
  { id: "mistralai/devstral-2512:free", name: "Mistral Devstral" },
  { id: "z-ai/glm-4.5-air:free", name: "Z-AI GLM 4.5 Air" },
] as const;

export type OpenRouterModel = (typeof OPENROUTER_MODELS)[number]["id"];

export interface AISettings {
  geminiKey: string;
  openrouterKey: string;
  activeProvider: AIProvider;
  openrouterModel: OpenRouterModel;
  enableModelFallback: boolean;
}

interface AISettingsContextValue {
  settings: AISettings;
  updateGeminiKey: (key: string) => void;
  updateOpenRouterKey: (key: string) => void;
  setActiveProvider: (provider: AIProvider) => void;
  setOpenRouterModel: (model: OpenRouterModel) => void;
  setEnableModelFallback: (enabled: boolean) => void;
  hasValidKey: () => boolean;
  getActiveKey: () => string;
  clearAllKeys: () => void;
}

const STORAGE_KEY = "PDFWorkSpace-ai-settings";

const defaultSettings: AISettings = {
  geminiKey: "",
  openrouterKey: "",
  activeProvider: "gemini",
  openrouterModel: "liquid/lfm-2.5-1.2b-thinking:free",
  enableModelFallback: true,
};

const AISettingsContext = createContext<AISettingsContextValue | undefined>(
  undefined,
);

export const AISettingsProvider = ({ children }: { children: ReactNode }) => {
  const [settings, setSettings] = useState<AISettings>(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        return { ...defaultSettings, ...JSON.parse(stored) };
      }
    } catch {
      // Ignore parse errors
    }
    return defaultSettings;
  });

  // Persist to localStorage whenever settings change
  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(settings));
  }, [settings]);

  const updateGeminiKey = (key: string) => {
    setSettings((prev) => ({ ...prev, geminiKey: key.trim() }));
  };

  const updateOpenRouterKey = (key: string) => {
    setSettings((prev) => ({ ...prev, openrouterKey: key.trim() }));
  };

  const setActiveProvider = (provider: AIProvider) => {
    setSettings((prev) => ({ ...prev, activeProvider: provider }));
  };

  const setOpenRouterModel = (model: OpenRouterModel) => {
    setSettings((prev) => ({ ...prev, openrouterModel: model }));
  };

  const setEnableModelFallback = (enabled: boolean) => {
    setSettings((prev) => ({ ...prev, enableModelFallback: enabled }));
  };

  const hasValidKey = () => {
    const key = getActiveKey();
    return key.length > 10;
  };

  const getActiveKey = () => {
    return settings.activeProvider === "gemini"
      ? settings.geminiKey
      : settings.openrouterKey;
  };

  const clearAllKeys = () => {
    setSettings(defaultSettings);
    localStorage.removeItem(STORAGE_KEY);
  };

  return (
    <AISettingsContext.Provider
      value={{
        settings,
        updateGeminiKey,
        updateOpenRouterKey,
        setActiveProvider,
        setOpenRouterModel,
        setEnableModelFallback,
        hasValidKey,
        getActiveKey,
        clearAllKeys,
      }}
    >
      {children}
    </AISettingsContext.Provider>
  );
};

export const useAISettings = () => {
  const ctx = useContext(AISettingsContext);
  if (!ctx) {
    throw new Error("useAISettings must be used within AISettingsProvider");
  }
  return ctx;
};
